import { useEffect, useMemo, useState } from 'react';
import { setupAPI, telemetryAPI } from '../../api/client';
import type { AIProvider, TrackingMode } from '../../types';

const PROVIDER_LABELS: Record<AIProvider, string> = {
  managed: 'Clawer AI',
  gemini: 'Google Gemini',
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  ollama: 'Ollama (local)',
};

interface ReviewCompleteStepProps {
  whatsappConnected: boolean;
  aiProvider: AIProvider;
  aiApiKey: string;
  aiModel: string;
  ollamaUrl: string;
  keyValidated: boolean;
  trackingMode: TrackingMode;
  sharingTier: number;
  timezone: string;
  phoneNumber: string;
  userLanguage?: string;
  onTimezoneChange: (tz: string) => void;
  onPhoneChange: (phone: string) => void;
  onComplete: () => void;
  onBack: () => void;
}

export function ReviewCompleteStep({
  whatsappConnected,
  aiProvider,
  aiApiKey,
  aiModel,
  keyValidated,
  trackingMode,
  sharingTier,
  timezone,
  phoneNumber,
  userLanguage = 'en',
  onTimezoneChange,
  onPhoneChange,
  onComplete,
  onBack,
}: ReviewCompleteStepProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Build timezone list from browser
  const timezones = useMemo(() => {
    try {
      return (Intl as unknown as { supportedValuesOf: (key: string) => string[] }).supportedValuesOf('timeZone');
    } catch {
      return [
        'UTC', 'America/New_York', 'America/Chicago', 'America/Denver',
        'America/Los_Angeles', 'America/Sao_Paulo', 'America/Mexico_City',
        'Europe/London', 'Europe/Paris', 'Europe/Berlin', 'Europe/Moscow',
        'Africa/Cairo', 'Africa/Johannesburg', 'Asia/Dubai', 'Asia/Kolkata',
        'Asia/Shanghai', 'Asia/Singapore', 'Asia/Tokyo', 'Asia/Seoul',
        'Australia/Sydney', 'Pacific/Auckland',
      ];
    }
  }, []);

  // Auto-detect timezone on first render
  useEffect(() => {
    if (!timezone) {
      try {
        const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (detected) onTimezoneChange(detected);
      } catch {
        onTimezoneChange('UTC');
      }
    }
  }, [timezone, onTimezoneChange]);

  async function handleComplete() {
    // Validate phone if provided
    if (phoneNumber && !/^\+[0-9]{7,15}$/.test(phoneNumber)) {
      setError('Phone number must be in E.164 format (e.g. +6588123456)');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const result = await setupAPI.complete({
        ai_provider: aiProvider,
        ai_api_key: aiProvider === 'ollama' || aiProvider === 'managed' ? '' : aiApiKey,
        ai_model: aiModel,
        tracking_mode: trackingMode,
        timezone: timezone || 'UTC',
        phone_number: phoneNumber,
        user_language: userLanguage,
      });
      if (result.success) {
        // Store auth token for subsequent API calls
        if (result.auth_token) {
          localStorage.setItem('clawer_auth_token', result.auth_token);
        }
        if (result.gateway_token) {
          localStorage.setItem('clawer_gateway_token', result.gateway_token);
        }
        // Save telemetry sharing tier
        try {
          await telemetryAPI.updateConfig(sharingTier);
        } catch {
          // Non-blocking — sharing tier can be configured later in Settings
        }
        onComplete();
      } else {
        setError(result.error || 'Setup failed. Please try again.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Setup failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <p className="text-xs font-medium text-ink-tertiary uppercase tracking-wider mb-1" data-ph-no-mask>
        Step 6 of 6
      </p>
      <h1 className="text-2xl font-bold text-ink mb-1" data-ph-no-mask>Review & Complete</h1>
      <p className="text-sm text-ink-secondary mb-6 leading-relaxed" data-ph-no-mask>
        Review your selections and add a few final details.
      </p>

      {/* Summary card */}
      <div className="rounded-xl bg-canvas border border-edge p-4 mb-6">
        <p className="text-xs font-medium text-ink-tertiary uppercase tracking-wider mb-3" data-ph-no-mask>
          Your selections
        </p>
        <div className="space-y-2">
          <SummaryRow
            label="WhatsApp"
            value={whatsappConnected ? 'Connected' : 'Not connected'}
            valueColor={whatsappConnected ? 'text-success' : 'text-warning'}
          />
          <SummaryRow label="AI Provider" value={PROVIDER_LABELS[aiProvider]} />
          <SummaryRow label="Model" value={aiModel} />
          <SummaryRow
            label={aiProvider === 'managed' ? 'Authentication' : 'API Key'}
            value={aiProvider === 'managed' ? 'Auto-configured' : aiProvider === 'ollama' ? 'Not needed' : keyValidated ? 'Validated' : 'Not validated'}
            valueColor={aiProvider === 'managed' ? 'text-success' : aiProvider === 'ollama' ? 'text-ink-secondary' : keyValidated ? 'text-success' : 'text-warning'}
          />
          <SummaryRow
            label="Tracking"
            value={trackingMode === 'opt_out' ? 'Opt-out (all contacts)' : 'Opt-in (manual)'}
          />
          <SummaryRow
            label="Data Sharing"
            value={sharingTier >= 1 ? 'Anonymous metrics' : 'Off'}
          />
        </div>
      </div>

      {!whatsappConnected && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm mb-4">
          <span className="material-icons-round text-amber-500 text-lg shrink-0 mt-0.5">info</span>
          <p className="text-amber-800">
            WhatsApp is not connected. You can connect it later in Settings.
            Message features won&apos;t work until connected.
          </p>
        </div>
      )}

      {/* Timezone */}
      <label className="block text-sm font-medium text-ink mb-1" data-ph-no-mask>Timezone</label>
      <select
        value={timezone}
        onChange={(e) => onTimezoneChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg border border-edge text-sm focus:outline-none focus:ring-2 focus:ring-accent/20 mb-4"
      >
        {timezones.map((tz: string) => (
          <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
        ))}
      </select>

      {/* Phone number */}
      <label className="block text-sm font-medium text-ink mb-1" data-ph-no-mask>
        WhatsApp Phone Number{' '}
        <span className="text-ink-tertiary font-normal">(optional)</span>
      </label>
      <input
        type="tel"
        value={phoneNumber}
        onChange={(e) => { onPhoneChange(e.target.value); setError(''); }}
        placeholder="+6588123456"
        className="w-full px-3 py-2 rounded-lg border border-edge text-sm focus:outline-none focus:ring-2 focus:ring-accent/20 mb-4"
      />

      {/* Error */}
      {error && (
        <p className="text-xs text-error mb-4">{error}</p>
      )}

      {/* Navigation */}
      <div className="flex gap-3 mt-2">
        <button
          onClick={onBack}
          disabled={submitting}
          className="px-4 py-2.5 rounded-lg border border-edge text-sm font-medium text-ink hover:bg-canvas transition-colors disabled:opacity-50"
          data-ph-no-mask
        >
          Back
        </button>
        <button
          onClick={handleComplete}
          disabled={submitting}
          className="px-5 py-2.5 rounded-lg bg-accent text-white text-sm font-medium hover:bg-accent-deep transition-colors disabled:opacity-50"
          data-ph-no-mask
        >
          {submitting ? (
            <span className="flex items-center gap-2">
              <span className="material-icons-round text-sm animate-spin">sync</span>
              Saving...
            </span>
          ) : (
            <span data-ph-no-mask>Complete Setup</span>
          )}
        </button>
      </div>
    </div>
  );
}

function SummaryRow({
  label,
  value,
  valueColor = 'text-ink',
}: {
  label: string;
  value: string;
  valueColor?: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-ink-secondary" data-ph-no-mask>{label}</span>
      <span className={`text-xs font-medium ${valueColor}`}>{value}</span>
    </div>
  );
}
